import { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { Handshake, Loader2, CheckCircle2, XCircle, FileText, PenSquare, Download, ExternalLink } from 'lucide-react';
import api, { API } from '../api';
import { useToast } from '../hooks/use-toast';

const I18N = {
  fr: {
    badge: 'Accord de collaboration',
    subtitle: 'Yuville International vous invite à consulter et signer l\'accord de collaboration encadrant l\'introduction de clients à votre entreprise.',
    docLabel: 'Document contractuel',
    openPdf: 'Ouvrir en PDF',
    signPrompt: 'Prenez connaissance de l\'accord, puis signez pour formaliser notre collaboration.',
    signBtn: 'Signer l\'accord',
    signDialogTitle: 'Signature de l\'accord',
    chooseMethod: 'Choisissez la méthode de signature :',
    methodDs: 'Signature électronique', methodDsHint: 'DocuSign — recommandée',
    methodInternal: 'Acceptation en ligne', methodInternalHint: 'nom · entreprise · horodatage',
    phName: 'Nom du signataire', phCompany: 'Entreprise', phEmail: 'Courriel professionnel',
    readConfirm: "J'ai lu l'intégralité de l'accord et je l'accepte au nom de l'entreprise indiquée.",
    cancel: 'Annuler', submitDs: 'Signer via DocuSign', submitInternal: 'Accepter',
    acceptedTitle: 'Accord accepté',
    acceptedOn: 'Accepté le', acceptedMethod: 'Méthode',
    methodInternalLabel: 'signature interne',
    downloadSigned: 'Télécharger le document signé',
    errAccess: 'Accès impossible', back: 'Retour à l\'accueil',
    missing: 'Champs manquants', missingDesc: 'Merci de compléter tous les champs.',
    error: 'Erreur',
    statusLabels: { to_prepare: 'À préparer', sent: 'Envoyé', waiting_acceptance: 'En attente', accepted: 'Accepté', refused: 'Refusé', expired: 'Expiré' },
  },
  en: {
    badge: 'Collaboration agreement',
    subtitle: 'Yuville International invites you to review and sign the collaboration agreement framing the introduction of clients to your company.',
    docLabel: 'Contractual document',
    openPdf: 'Open PDF',
    signPrompt: 'Review the agreement, then sign to formalize our collaboration.',
    signBtn: 'Sign the agreement',
    signDialogTitle: 'Sign the agreement',
    chooseMethod: 'Choose a signing method:',
    methodDs: 'Electronic signature', methodDsHint: 'DocuSign — recommended',
    methodInternal: 'Online acceptance', methodInternalHint: 'name · company · timestamp',
    phName: 'Signer name', phCompany: 'Company', phEmail: 'Business email',
    readConfirm: 'I have read the entire agreement and I accept it on behalf of the company above.',
    cancel: 'Cancel', submitDs: 'Sign via DocuSign', submitInternal: 'Accept',
    acceptedTitle: 'Agreement accepted',
    acceptedOn: 'Accepted on', acceptedMethod: 'Method',
    methodInternalLabel: 'internal signature',
    downloadSigned: 'Download the signed document',
    errAccess: 'Access denied', back: 'Back to home',
    missing: 'Missing fields', missingDesc: 'Please complete all fields.',
    error: 'Error',
    statusLabels: { to_prepare: 'To prepare', sent: 'Sent', waiting_acceptance: 'Awaiting signature', accepted: 'Accepted', refused: 'Declined', expired: 'Expired' },
  },
  zh: {
    badge: '合作协议',
    subtitle: 'Yuville International 邀请您查阅并签署规范向贵司介绍客户的合作协议。',
    docLabel: '合同性文件',
    openPdf: '打开 PDF',
    signPrompt: '请阅读协议，然后签署以正式确立合作关系。',
    signBtn: '签署协议',
    signDialogTitle: '签署协议',
    chooseMethod: '请选择签名方式：',
    methodDs: '电子签名', methodDsHint: '推荐使用 DocuSign',
    methodInternal: '在线接受', methodInternalHint: '姓名 · 公司 · 时间戳',
    phName: '签字人姓名', phCompany: '公司', phEmail: '工作邮箱',
    readConfirm: '本人已阅读完整协议，并以上述公司名义接受其条款。',
    cancel: '取消', submitDs: '通过 DocuSign 签署', submitInternal: '接受',
    acceptedTitle: '协议已接受',
    acceptedOn: '接受时间：', acceptedMethod: '方式',
    methodInternalLabel: '内部签名',
    downloadSigned: '下载已签署的文件',
    errAccess: '无法访问', back: '返回首页',
    missing: '字段缺失', missingDesc: '请填写所有字段。',
    error: '错误',
    statusLabels: { to_prepare: '待准备', sent: '已发送', waiting_acceptance: '待签署', accepted: '已接受', refused: '已拒绝', expired: '已过期' },
  },
};

/**
 * Manufacturer-facing page — reads the agreement's own `lang` field and uses it
 * (independent of the site-wide language switcher).
 */
export default function ManufacturerAgreementPage() {
  const { mid } = useParams();
  const [sp] = useSearchParams();
  const token = sp.get('token') || '';
  const { toast } = useToast();

  const [state, setState] = useState({ loading: true, error: '', data: null });
  const [lang, setLang] = useState('fr');
  const [signOpen, setSignOpen] = useState(false);
  const [method, setMethod] = useState('docusign');
  const [read, setRead] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [signerCompany, setSignerCompany] = useState('');
  const [signerEmail, setSignerEmail] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const res = await api.get(`/manufacturer/agreement/${mid}?token=${encodeURIComponent(token)}`);
      setState({ loading: false, error: '', data: res.data });
      setLang(res.data.lang || 'fr');
      const a = res.data.agreement || {};
      setSignerName((p) => p || a.signerName || '');
      setSignerCompany((p) => p || a.signerCompany || '');
      setSignerEmail((p) => p || a.signerEmail || '');
    } catch (e) {
      setState({ loading: false, error: e?.response?.data?.detail || 'Lien invalide ou expiré.', data: null });
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [mid, token]);

  const T = I18N[lang] || I18N.fr;
  const isAccepted = state.data?.agreement?.status === 'accepted';
  const isReturnFromDs = window.location.pathname.endsWith('/signature-complete');
  useEffect(() => {
    if (isReturnFromDs && !isAccepted && state.data) finalizeDs(true);
    // eslint-disable-next-line
  }, [isReturnFromDs, state.data?.agreement?.id]);

  const startDs = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/manufacturer/agreement/${mid}/docusign/start?token=${encodeURIComponent(token)}`);
      window.location.href = res.data.signingUrl;
    } catch (e) { toast({ title: 'DocuSign', description: e?.response?.data?.detail || 'Impossible', duration: 15000 }); setBusy(false); }
  };
  const finalizeDs = async (silent = false) => {
    try {
      const res = await api.post(`/manufacturer/agreement/${mid}/docusign/finalize?token=${encodeURIComponent(token)}`);
      if (res.data?.status === 'accepted') { toast({ title: T.acceptedTitle, description: '✓', duration: 10000 }); load(); }
      else if (!silent) toast({ title: 'DocuSign', description: 'En attente…', duration: 8000 });
    } catch (e) { if (!silent) toast({ title: 'DocuSign', description: e?.response?.data?.detail || '', duration: 8000 }); }
  };
  const acceptInternal = async () => {
    if (!read || !signerName.trim() || !signerCompany.trim() || !signerEmail.trim()) {
      toast({ title: T.missing, description: T.missingDesc, duration: 8000 }); return;
    }
    setBusy(true);
    try {
      await api.post(`/manufacturer/agreement/${mid}/accept-internal?token=${encodeURIComponent(token)}`, {
        signerName, signerCompany, signerEmail, readConfirmed: read,
      });
      toast({ title: T.acceptedTitle, description: '✓', duration: 10000 });
      setSignOpen(false); load();
    } catch (e) { toast({ title: T.error, description: e?.response?.data?.detail || '', duration: 10000 }); }
    finally { setBusy(false); }
  };

  if (state.loading) return <div className="min-h-screen flex items-center justify-center bg-gray-50"><Loader2 className="w-8 h-8 animate-spin text-brand-yellow" /></div>;
  if (state.error) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-6">
      <div className="max-w-md text-center bg-white p-8 rounded-xl shadow-md border border-gray-200">
        <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h1 className="text-xl font-bold text-brand-navy mb-2">{T.errAccess}</h1>
        <p className="text-gray-600 text-sm mb-6">{state.error}</p>
        <Link to="/" className="text-brand-navy hover:text-brand-yellow text-sm font-semibold">← {T.back}</Link>
      </div>
    </div>
  );

  const a = state.data.agreement;
  const pdfUrl = `${API}/manufacturer/agreement/${mid}/pdf?token=${encodeURIComponent(token)}`;
  const dateLocale = lang === 'zh' ? 'zh-CN' : lang === 'en' ? 'en-CA' : 'fr-CA';

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <header className="bg-brand-navy text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-8">
          <Link to="/" className="text-brand-yellow text-xs font-bold tracking-widest uppercase">← Yuville International</Link>
          <div className="inline-flex items-center gap-2 mt-3 mb-2 px-3 py-1 rounded-full bg-brand-yellow/20 border border-brand-yellow text-brand-yellow text-[11px] font-bold tracking-widest uppercase">
            <Handshake className="w-3 h-3" /> {T.badge}
          </div>
          <h1 data-testid="ma-title" className="text-3xl md:text-4xl font-bold leading-tight">{a.title}</h1>
          <p className="mt-3 text-white/80 max-w-2xl leading-relaxed">{T.subtitle}</p>
        </div>
      </header>

      {isAccepted && (
        <div className="max-w-4xl mx-auto px-6 lg:px-10 mt-6">
          <div className="bg-green-50 border-2 border-green-400 rounded-xl p-6" data-testid="ma-accepted-banner">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-6 h-6 text-green-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <h2 className="font-bold text-brand-navy text-lg">{T.acceptedTitle}</h2>
                <p className="text-sm text-gray-700 mt-1">
                  {T.acceptedOn} {new Date(a.acceptedAt).toLocaleString(dateLocale)} — {T.acceptedMethod}: {a.method === 'docusign' ? 'DocuSign' : T.methodInternalLabel}
                </p>
                <a href={pdfUrl} className="inline-flex items-center gap-1.5 mt-3 text-brand-navy hover:text-brand-yellow text-sm font-semibold underline" download>
                  <Download className="w-4 h-4" /> {T.downloadSigned}
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      <section className="max-w-4xl mx-auto px-6 lg:px-10 mt-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="bg-brand-navy/5 border-b border-gray-100 px-6 py-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-brand-yellow font-bold">{T.docLabel} · v{a.version}</div>
              <h2 className="text-brand-navy font-bold text-lg">{a.title}</h2>
            </div>
            <a href={pdfUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-brand-navy hover:text-brand-yellow text-sm font-semibold" data-testid="download-ma-pdf">
              <FileText className="w-4 h-4" /> {T.openPdf} <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          <div className="p-6 md:p-10 max-h-[65vh] overflow-y-auto" data-testid="ma-content">
            <div className="prose max-w-none text-brand-navy" dangerouslySetInnerHTML={{ __html: a.content }} />
          </div>
        </div>
      </section>

      {!isAccepted && (
        <div className="fixed bottom-0 inset-x-0 bg-white border-t border-gray-200 shadow-[0_-4px_16px_rgba(0,0,0,0.05)] z-40">
          <div className="max-w-4xl mx-auto px-6 lg:px-10 py-3 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="text-sm text-gray-600">{T.signPrompt}</div>
            <button data-testid="open-ma-sign-btn" onClick={() => setSignOpen(true)} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-yellow text-brand-navy text-sm font-bold hover:bg-yellow-400 transition">
              <PenSquare className="w-4 h-4" /> {T.signBtn}
            </button>
          </div>
        </div>
      )}

      {signOpen && !isAccepted && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => !busy && setSignOpen(false)}>
          <div className="bg-white rounded-2xl max-w-lg w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="bg-brand-navy text-white px-6 py-4 flex items-center justify-between">
              <h2 className="font-bold">{T.signDialogTitle}</h2>
              <button disabled={busy} onClick={() => setSignOpen(false)} className="text-white/80 hover:text-white disabled:opacity-40"><XCircle className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="text-sm text-gray-600">{T.chooseMethod}</div>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => setMethod('docusign')} className={`p-3 rounded-lg border text-left transition ${method === 'docusign' ? 'border-brand-navy bg-brand-navy/5' : 'border-gray-200 hover:border-brand-navy'}`}>
                  <div className="font-bold text-brand-navy text-sm">{T.methodDs}</div>
                  <div className="text-[11px] text-gray-500">{T.methodDsHint}</div>
                </button>
                <button onClick={() => setMethod('internal')} className={`p-3 rounded-lg border text-left transition ${method === 'internal' ? 'border-brand-navy bg-brand-navy/5' : 'border-gray-200 hover:border-brand-navy'}`}>
                  <div className="font-bold text-brand-navy text-sm">{T.methodInternal}</div>
                  <div className="text-[11px] text-gray-500">{T.methodInternalHint}</div>
                </button>
              </div>
              <div className="grid grid-cols-1 gap-3 pt-2">
                <input value={signerName} onChange={(e) => setSignerName(e.target.value)} placeholder={T.phName} className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-brand-navy" />
                <input value={signerCompany} onChange={(e) => setSignerCompany(e.target.value)} placeholder={T.phCompany} className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-brand-navy" />
                <input type="email" value={signerEmail} onChange={(e) => setSignerEmail(e.target.value)} placeholder={T.phEmail} className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-brand-navy" />
              </div>
              <label className="flex items-start gap-2 text-xs text-gray-600 cursor-pointer">
                <input type="checkbox" checked={read} onChange={(e) => setRead(e.target.checked)} className="w-4 h-4 mt-0.5 accent-brand-yellow" />
                <span>{T.readConfirm}</span>
              </label>
              <div className="flex gap-2 pt-2">
                <button disabled={busy} onClick={() => setSignOpen(false)} className="flex-1 px-4 py-2.5 rounded-full border border-gray-300 text-brand-navy text-sm font-semibold hover:bg-gray-50 disabled:opacity-50">{T.cancel}</button>
                {method === 'docusign' ? (
                  <button disabled={busy || !read || !signerEmail} onClick={startDs} className="flex-1 px-4 py-2.5 rounded-full bg-brand-yellow text-brand-navy text-sm font-bold hover:bg-yellow-400 disabled:opacity-50 flex items-center justify-center gap-2">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PenSquare className="w-4 h-4" />} {T.submitDs}
                  </button>
                ) : (
                  <button disabled={busy || !read || !signerName.trim() || !signerCompany.trim() || !signerEmail.trim()} onClick={acceptInternal} className="flex-1 px-4 py-2.5 rounded-full bg-brand-yellow text-brand-navy text-sm font-bold hover:bg-yellow-400 disabled:opacity-50 flex items-center justify-center gap-2">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} {T.submitInternal}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
